// Curated SQLite keyword list emitted by `sqliteResolver`. Tier 7 per
// RESEARCH.md §5.2. The orchestrator's `applyKeywordCasingPostProcess`
// transforms label/insertText/filterText to the user's preferred casing —
// labels stay UPPER here so the dominant-case fallback has a stable
// reference and matches the conventional rendering.
//
// Selection criteria: SQLite keywords (per sqlite.org "Keywords") that
// surface in normal user authoring flows (DML, DDL, query-shaping, common
// modern SQLite forms like UPSERT / RETURNING / WITH RECURSIVE / WINDOW).

const SQLITE_KEYWORD_LIST: readonly string[] = [
  'ABORT', 'ACTION', 'ADD', 'AFTER', 'ALL', 'ALTER', 'ALWAYS', 'ANALYZE',
  'AND', 'AS', 'ASC', 'ATTACH', 'AUTOINCREMENT', 'BEFORE', 'BEGIN',
  'BETWEEN', 'BY', 'CASCADE', 'CASE', 'CAST', 'CHECK', 'COLLATE',
  'COLUMN', 'COMMIT', 'CONFLICT', 'CONSTRAINT', 'CREATE', 'CROSS',
  'CURRENT', 'CURRENT_DATE', 'CURRENT_TIME', 'CURRENT_TIMESTAMP',
  'DATABASE', 'DEFAULT', 'DEFERRABLE', 'DEFERRED', 'DELETE', 'DESC',
  'DETACH', 'DISTINCT', 'DO', 'DROP', 'EACH', 'ELSE', 'END', 'ESCAPE',
  'EXCEPT', 'EXCLUDE', 'EXCLUSIVE', 'EXISTS', 'EXPLAIN', 'FAIL', 'FILTER',
  'FIRST', 'FOLLOWING', 'FOR', 'FOREIGN', 'FROM', 'FULL', 'GENERATED',
  'GLOB', 'GROUP', 'GROUPS', 'HAVING', 'IF', 'IGNORE', 'IMMEDIATE', 'IN',
  'INDEX', 'INDEXED', 'INITIALLY', 'INNER', 'INSERT', 'INSTEAD',
  'INTERSECT', 'INTO', 'IS', 'ISNULL', 'JOIN', 'KEY', 'LAST', 'LEFT',
  'LIKE', 'LIMIT', 'MATCH', 'MATERIALIZED', 'NATURAL', 'NO', 'NOT',
  'NOTHING', 'NOTNULL', 'NULL', 'NULLS', 'OF', 'OFFSET', 'ON', 'OR',
  'ORDER', 'OTHERS', 'OUTER', 'OVER', 'PARTITION', 'PLAN', 'PRAGMA',
  'PRECEDING', 'PRIMARY', 'QUERY', 'RAISE', 'RANGE', 'RECURSIVE',
  'REFERENCES', 'REGEXP', 'REINDEX', 'RELEASE', 'RENAME', 'REPLACE',
  'RESTRICT', 'RETURNING', 'RIGHT', 'ROLLBACK', 'ROW', 'ROWS', 'SAVEPOINT',
  'SELECT', 'SET', 'TABLE', 'TEMP', 'TEMPORARY', 'THEN', 'TIES', 'TO',
  'TRANSACTION', 'TRIGGER', 'UNBOUNDED', 'UNION', 'UNIQUE', 'UPDATE',
  'USING', 'VACUUM', 'VALUES', 'VIEW', 'VIRTUAL', 'WHEN', 'WHERE',
  'WINDOW', 'WITH', 'WITHOUT',
  // SQLite type names commonly typed in DDL — surface as keyword-tier so
  // they appear alongside the rest of the curated set rather than only
  // when fuzzy-matched against built-ins. Includes the STRICT-mode types,
  // common flexible-mode names (`NUMERIC`, `VARCHAR`, `BOOLEAN`, `DATE`,
  // `DATETIME`), and the affinity-token `ANY`. STRICT itself stays as a
  // table-option keyword.
  'ANY', 'BLOB', 'BOOLEAN', 'DATE', 'DATETIME', 'INT', 'INTEGER',
  'NUMERIC', 'REAL', 'STRICT', 'TEXT', 'VARCHAR',
  // Collation names — surfaced at tier 7 as a fallback so they show up
  // even when the COLLATE-slot detector is off (e.g. on an `ORDER BY x
  // COLLATE |` form that the detector hasn't yet seen). The COLLATE slot
  // detector promotes these to tier 1.
  'BINARY', 'NOCASE', 'RTRIM',
  // Generated-column storage class — `VIRTUAL` is already in the list
  // above as a CREATE-modifier keyword; `STORED` is the GENERATED-column
  // counterpart. The generated-storage slot detector promotes both.
  'STORED',
];

export const SQLITE_KEYWORD_SET: ReadonlySet<string> = new Set(SQLITE_KEYWORD_LIST);

/**
 * Position-aware keyword list. Today returns the whole curated set — the
 * orchestrator's quality-filter trims the popup. A future per-position
 * refinement can split this into clause-start vs general subsets.
 */
export function getSqliteKeywordsForPosition(): readonly string[] {
  return SQLITE_KEYWORD_LIST;
}

/**
 * Multi-word SQLite keywords typed one word at a time — see
 * `compoundKeywordRemainder.ts`. Only compounds
 * `context/legalNextKeywords.ts` does not already carry for sqlite belong
 * here. Sources (sqlite.org, "Window Functions" — frame specification):
 *   • `UNBOUNDED FOLLOWING` — frame END bound; the per-slot model carries only
 *     the frame-START vocabulary (`after-window-frame-unit`), and the
 *     direction-scoped lists live in `sqliteResolver.ts`
 *     (`SQLITE_FRAME_START_KEYWORDS` / `SQLITE_FRAME_END_KEYWORDS`).
 *   • `NO OTHERS` — the default `EXCLUDE` option.
 */
export const SQLITE_COMPOUND_KEYWORDS: readonly string[] = [
  'UNBOUNDED FOLLOWING',
  'NO OTHERS',
];
